import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, CalendarRange, Search, ChevronDown, ChevronRight } from "lucide-react";
import { IDIOMAS, labelModalidadPortal } from "@/lib/constants";
import { nombreTourDisplay } from "@/lib/tourDisplay";

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const masDias = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// PROMPT 122: las agencias ven solo los cupos libres; el Portal ya no recibe la capacidad.
// El ambar (20% o menos libre) viene calculado del backend en `pocos`.
const cupoColor = (libre, pocos) => {
  if (libre <= 0) return "text-red-600 font-semibold";
  if (pocos) return "text-amber-600 font-semibold";
  return "text-emerald-600 font-semibold";
};
const libresTexto = (n) => `${n} ${n === 1 ? "libre" : "libres"}`;

const money = (v, m) => (v == null ? null : `${m === "USD" ? "$" : ""}${Number(v).toFixed(0)}${m && m !== "USD" ? " " + m : ""}`);

export default function PortalDisponibilidad() {
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [tour, setTour] = useState("todos");
  const [desde, setDesde] = useState(hoyISO());
  const [hasta, setHasta] = useState(masDias(hoyISO(), 14));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandidas, setExpandidas] = useState({});

  useEffect(() => {
    api.get("/portal/tours").then(({ data }) => setTours(data)).catch(() => {});
  }, []);

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { desde, hasta };
      if (tour !== "todos") params.tour_id = tour;
      const { data } = await api.get("/portal/disponibilidad", { params });
      setSlots(data.slots || []);
      // PROMPT 117: antes de la fecha habilitada no hay disponibilidad: los filtros pasan a
      // empezar en esa fecha (el aviso de arriba explica por que).
      if (data.reservas_desde && desde < data.reservas_desde) {
        setDesde(data.reservas_desde);
        if (hasta < data.reservas_desde) setHasta(masDias(data.reservas_desde, 14));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, tour]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const reservar = (s, opcionPreset) => {
    navigate("/portal/nueva", { state: { slot: s, presetOpcion: opcionPreset || null } });
  };
  const toggleExpand = (key) => setExpandidas((e) => ({ ...e, [key]: !e[key] }));

  return (
    <div className="space-y-5" data-testid="portal-disponibilidad">
      <div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <CalendarRange className="h-6 w-6 text-indigo-600" /> Disponibilidad en vivo
        </h2>
        <p className="text-sm text-slate-500 mt-1">Cupos libres por tour, fecha y horario. Reservá directo desde acá.</p>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Tour</Label>
            <Select value={tour} onValueChange={setTour}>
              <SelectTrigger data-testid="dispo-tour-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los tours</SelectItem>
                {tours.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="dispo-desde" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="dispo-hasta" />
          </div>
          <Button onClick={buscar} disabled={loading} className="gap-2" data-testid="dispo-buscar">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Buscar
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["", "Fecha", "Hora", "Tour", "Opciones", "Cupo libre", ""].map((h, i) => (
                  <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="h-32 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" /></TableCell></TableRow>
              ) : slots.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="h-32 text-center text-slate-400 text-sm" data-testid="dispo-empty">No hay salidas en el rango elegido.</TableCell></TableRow>
              ) : (
                slots.map((s, i) => {
                  const key = `${s.tour_id}|${s.fecha}|${s.hora}`;
                  const abierta = !!expandidas[key];
                  const multi = s.n_opciones > 1;
                  return (
                    <Fragment key={key}>
                      <TableRow data-testid="dispo-slot-row" className="hover:bg-slate-50">
                        <TableCell className="w-8">
                          {multi && (
                            <button onClick={() => toggleExpand(key)} data-testid={`dispo-expandir-${i}`} className="text-slate-400 hover:text-slate-700">
                              {abierta ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">{s.fecha}</TableCell>
                        <TableCell className="font-mono text-sm text-slate-900">{s.hora}</TableCell>
                        <TableCell className="text-sm font-medium text-slate-900">
                          {nombreTourDisplay(s.tour_nombre, !multi ? s.opciones[0]?.modalidad : null)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {multi ? (
                            <button onClick={() => toggleExpand(key)} className="text-indigo-600 font-medium hover:underline" data-testid={`dispo-nopciones-${i}`}>
                              {s.n_opciones} opciones (ver)
                            </button>
                          ) : (
                            <span>
                              {labelModalidadPortal(s.opciones[0].modalidad, s.opciones[0].vehiculo)}
                              {!s.es_privado && <span className="text-slate-400"> · {IDIOMAS[s.opciones[0].idioma] || s.opciones[0].idioma}</span>}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={`text-xl font-bold tabular-nums ${s.es_privado ? "text-violet-600 text-sm font-medium" : cupoColor(s.libre, s.pocos)}`} data-testid={`dispo-cupo-${i}`}>
                          {s.es_privado ? "Por solicitud" : (
                            <>
                              {s.libre}{" "}
                              <span className="text-xs font-medium">{s.libre === 1 ? "libre" : "libres"}</span>
                            </>
                          )}
                          {multi && <span className="block text-[11px] font-normal text-slate-400">principal, ver opciones</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!s.disponible}
                            onClick={() => reservar(s)}
                            data-testid="dispo-reservar-btn"
                          >
                            {s.disponible ? (s.es_privado ? "Solicitar" : "Reservar") : "Sin cupo"}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {multi && abierta && (
                        <TableRow className="bg-slate-50/70">
                          <TableCell colSpan={7} className="py-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={`dispo-opciones-${i}`}>
                              {s.opciones.map((o, j) => (
                                <button
                                  key={j}
                                  type="button"
                                  disabled={!o.disponible}
                                  onClick={() => reservar(s, o)}
                                  data-testid={`dispo-opcion-item-${i}-${j}`}
                                  className={`text-left rounded-md border px-3 py-2 bg-white transition-colors ${
                                    !o.disponible ? "opacity-50 cursor-not-allowed border-slate-100" : "border-slate-200 hover:border-indigo-300"
                                  }`}
                                >
                                  <div className="text-xs font-medium text-slate-800">
                                    {labelModalidadPortal(o.modalidad, o.vehiculo)}
                                    {!o.es_privado && <span className="text-slate-400"> · {IDIOMAS[o.idioma] || o.idioma}</span>}
                                  </div>
                                  <div className={`text-xs font-medium mt-0.5 ${o.es_privado ? "text-violet-600" : o.disponible ? "text-emerald-600" : "text-red-500"}`}>
                                    {o.es_privado ? "Por solicitud" : o.disponible ? libresTexto(o.libre) : "Sin cupo"}
                                  </div>
                                  {o.tiene_tarifa && (
                                    <div className="text-[11px] text-slate-400 mt-0.5">{money(o.precio_adulto, o.moneda)} adulto</div>
                                  )}
                                </button>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
